/**
 * Global error handling utilities
 * Shows appropriate toasts for different error types and logs to Datadog
 */

import { CanceledError, isAxiosError } from 'axios';
import { addToast, getDefaultErrorToast } from '@/composables/useToasts';
import type { Toast } from '@/types/toasts';
import { i18n } from '@/plugins/i18n';
import { datadogLogs } from '@datadog/browser-logs';
import { getLocalStorageItem } from './localStorage';
import { inflcrErrorCodes, type ApiV1, type InflcrErrorCode } from '@teamworksdev/influencer/core';
import { isBefore } from 'date-fns';
import { camelCase } from 'lodash-es';

const { t } = i18n.global;
const defaultToast = getDefaultErrorToast();

function isProd() {
	return import.meta.env.MODE.startsWith('production');
}

function isStaging() {
	return import.meta.env.MODE.startsWith('staging');
}

function customToastWithFallback(title?: string, description?: string): Omit<Toast, 'uuid'> {
	return {
		type: 'error',
		title: title || defaultToast.title,
		description: description || defaultToast.description,
	};
}

/**
 * Handle Axios-specific error codes (network errors, timeouts, etc.)
 */
function handleAxiosErrorCode(code?: string): void {
	if (!code) return;

	const axiosErrorHandlers: Record<string, () => void> = {
		// Request was cancelled - typically intentional, so don't show error
		ERR_CANCELED: () => {},

		// Network error - no internet connection or server unreachable
		ERR_NETWORK: () => {
			addToast({
				type: 'error',
				title: t('axiosErrors.errNetwork.title'),
				description: t('axiosErrors.errNetwork.message'),
			});
		},

		// Timeout error
		ECONNABORTED: () => {
			addToast({
				type: 'error',
				title: t('axiosErrors.timeout.title'),
				description: t('axiosErrors.timeout.description'),
			});
		},
	};

	axiosErrorHandlers[code]?.();
}

/**
 * Handle Influencer-specifc error codes
 */
function handleInflcrError(errorCode: InflcrErrorCode): void {
	const camelCode = inflcrErrorCodes.includes(errorCode) ? camelCase(errorCode) : 'default';
	addToast({
		type: 'error',
		title: t(`inflcrErrorCodes.${camelCode}.title`),
		description: t(`inflcrErrorCodes.${camelCode}.message`),
	});
}

/**
 * Handle HTTP status codes
 */
function handleHttpStatusCode(
	status: number,
	customToast?: {
		title?: string;
		description?: string;
	} | 'none',
): void {
	// If custom toast title/description provided, use them instead of status-specific ones
	if (customToast !== 'none' && (customToast?.title || customToast?.description)) {
		addToast(customToastWithFallback(customToast?.title, customToast?.description));
		return;
	}
	// Only handle specific status codes with custom messages
	// Everything else gets the default error message
	const statusHandlers: Record<number, () => void> = {
		// Unauthorized - token expired or invalid
		401: () => {
			const authToken = getLocalStorageItem<ApiV1.AuthToken>('inflcr-token');
			if (authToken?.expires_at && isBefore(new Date(authToken.expires_at), new Date())) {
				window.location.replace('/logout');
			}
		},

		// Not Found
		404: () => {
			addToast({
				type: 'error',
				title: t('genericErrorCodes.404.title'),
				description: t('genericErrorCodes.404.message'),
			});
		},

		// Gateway Timeout
		504: () => {
			addToast({
				type: 'error',
				title: t('genericErrorCodes.504.title'),
				description: t('genericErrorCodes.504.message'),
			});
		},
	};

	if (statusHandlers[status]) {
		statusHandlers[status]();
	} else {
		// All other status codes - show generic error
		addToast(defaultToast);
	}
}

/**
 * Main error handler function
 * Shows appropriate toast and logs to Datadog
 *
 * @param error - The error object to handle
 * @param customToast - Optional custom toast configuration
 */
export function handleError(error: unknown, customToast?: {
	title?: string;
	description?: string;
} | 'none'): void {
	// Don't handle cancelled requests
	if (error instanceof CanceledError) {
		return;
	}

	if (!isProd()) {
		console.error('[ErrorHandler]', error);
	}

	// Handle Axios errors
	if (isAxiosError(error)) {
		if (!error.config?.bypassErrorToast) {
			if (error.response) {
				if (error.response.data.code) {
					handleInflcrError(error.response.data.code);
				} else {
					handleHttpStatusCode(error.response.status, customToast);
				}
			} else if (customToast !== 'none' && (customToast?.title || customToast?.description)) {
				addToast(customToastWithFallback(customToast.title, customToast.description));
			} else {
				handleAxiosErrorCode(error.code);
			}
		}

		// Log to Datadog
		if (isProd() || isStaging()) {
			datadogLogs.logger.error(error.message, {
				error: {
					stack: error.stack,
					message: error.message,
				},
				status: error.response?.status,
				code: error.code,
				url: error.config?.url,
				method: error.config?.method,
			});
		}
	} else if (error instanceof Error) {
		// Generic JavaScript error
		if (customToast !== 'none') {
			addToast(customToastWithFallback(customToast?.title, customToast?.description));
		}

		if (isProd() || isStaging()) {
			datadogLogs.logger.error(error.message, {
				error: {
					stack: error.stack,
					message: error.message,
				},
			});
		}
	} else {
		// Unknown error type
		if (customToast !== 'none') {
			addToast(customToastWithFallback(customToast?.title, customToast?.description));
		}

		if (isProd() || isStaging()) {
			datadogLogs.logger.error('Unknown error type', {
				error: String(error),
			});
		}
	}
}
